import { useAppDispatch, useTranslate, useAppSelector } from '@src/core/hooks';
import { setStepCheckout } from '@src/features/checkout';
import { Transactable, loadPaymentOptions, paymentOptionSelector } from '@src/features/payment';
import {
  Order,
  Sale,
  PaymentOption,
  DirectLink,
  PaymentMethodCode,
  PaymentFingerprint,
  OrderStatus,
  SaleState,
} from '@src/core/buyerApiClient';
import { setupFingerprint } from '@src/common/fingerprint';
import React, { useEffect, useState, useMemo } from 'react';
import { useWidgetId } from '@src/core/IdProvider';
import { track } from '@src/services/analytics';
import { ExpiredSales, PaymentMethods } from './styles';
import PaymentMethod from './PaymentMethod';
import PaymentSuccess from './PaymentSuccess';
import { ProviderFinancialError } from '@src/components/Checkout/ProviderFinancialError';
import Loading from '@src/components/ui2/Loading';

interface PaymentFormProps {
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
  onSuccess?: () => void;
}

const validPaymentMethodCodes: PaymentMethodCode[] = [
  PaymentMethodCode.CreditCard,
  PaymentMethodCode.DebitCard,
  PaymentMethodCode.Pix,
];

const sortPaymentOptions = (opt: PaymentOption) => (opt.paymentMethod.code === PaymentMethodCode.Pix ? -1 : 1);

const transactableIsExpired = (tr: Transactable) => {
  if (tr.order) {
    return !tr.order.isPayable;
  }

  if (tr.sale) {
    return !tr.sale.isPayable && tr.sale.state !== SaleState.PaymentComplete;
  }

  if (tr.directLink) {
    return tr.directLink.isExpired;
  }

  return false;
};

const transactableIsCreated = (tr: Transactable) => {
  if (tr.order) {
    return tr.order.status === OrderStatus.Created;
  }

  if (tr.sale) {
    return tr.sale.state === SaleState.Created;
  }

  if (tr.directLink) {
    return tr.directLink.status === SaleState.Created;
  }

  return false;
};

const PaymentStep = (props: PaymentFormProps) => {
  const { order, sale, directLink, onSuccess = () => {} } = props;

  const dispatch = useAppDispatch();
  const { i18nElement } = useTranslate();
  const widgetId = useWidgetId();

  const [selectedOption, setSelectedOption] = useState<PaymentOption | null>(null);

  const transactable = {
    order,
    sale,
    directLink,
  };

  const transactablePaymentOptions = useAppSelector(paymentOptionSelector(transactable));
  const validMethods = useMemo(() => {
    return (
      transactablePaymentOptions?.list
        .filter((opt) => validPaymentMethodCodes.includes(opt.paymentMethod.code))
        ?.sort(sortPaymentOptions) ?? []
    );
  }, [transactablePaymentOptions?.list]);

  const isSelected = (opt: PaymentOption) =>
    selectedOption != null && opt.paymentMethod.id === selectedOption.paymentMethod.id;

  const isExpired = transactableIsExpired(transactable);
  const isCreated = transactableIsCreated(transactable);
  const isPaymentSuccess =
    order?.status === OrderStatus.Confirmed ||
    sale?.state === SaleState.PaymentComplete ||
    directLink?.status === SaleState.PaymentComplete;

  useEffect(() => {
    track('checkoutPaymentFormAccess');

    dispatch(setStepCheckout({ id: widgetId || '', value: 'payment' }));
  }, []);

  useEffect(() => {
    if (!transactablePaymentOptions) {
      dispatch(loadPaymentOptions(transactable));
    }
  }, [transactable, transactablePaymentOptions]);

  useEffect(() => {
    if (transactablePaymentOptions && !transactablePaymentOptions.loading) {
      const fingerprints: { [key: string]: PaymentFingerprint } = {};
      for (const opt of validMethods) {
        if (opt.fingerprints) {
          for (const fingerprint of opt.fingerprints) {
            fingerprints[fingerprint.type] = fingerprint;
          }
        }
      }

      for (const key in fingerprints) {
        const fp = fingerprints[key]!;
        setupFingerprint(fp);
      }
    }
  }, [transactablePaymentOptions?.loading]);

  useEffect(() => {
    if (validMethods.length > 0 && !selectedOption) {
      setSelectedOption(validMethods[0]!);
    }
  }, [validMethods]);

  useEffect(() => {
    if (isPaymentSuccess) {
      onSuccess();
    }
  }, [isPaymentSuccess]);

  return (
    <>
      {isExpired ? (
        <ExpiredSales>
          <p>
            <strong>{i18nElement('checkout_expiredSale')}</strong>
          </p>

          <p>{i18nElement('checkout_makeYourPurchaseAgain')}</p>
        </ExpiredSales>
      ) : null}

      {!isExpired && isCreated ? (
        validMethods.length > 0 ? (
          <PaymentMethods>
            {validMethods.map((opt) => (
              <PaymentMethod
                key={opt.paymentMethod.id}
                order={order!}
                sale={sale!}
                directLink={directLink!}
                paymentOption={opt}
                selected={isSelected(opt)}
                onClick={() => setSelectedOption(opt)}
              />
            ))}
          </PaymentMethods>
        ) : transactablePaymentOptions?.loading ? (
          <Loading size={24} fullWidth={false} />
        ) : !isPaymentSuccess ? (
          <ProviderFinancialError horizontalStyle={false} />
        ) : null
      ) : null}

      {!isExpired && isPaymentSuccess ? <PaymentSuccess sale={sale} order={order} /> : null}
    </>
  );
};

export default PaymentStep;
